import type { Node as PMNode } from 'prosemirror-model';

/**
 * STUB / DESIGN SPIKE — the runtime shape of a compliance "fix" overlaid on a document.
 *
 * This mirrors the backend `AssessmentFinding` we'll store on `Document.Assessment.Findings`:
 *  - WHERE: a durable `anchor` (in production, Word `w14:paraId` + search text + occurrence; here we stub with
 *    just the search text, resolved to a live ProseMirror range at display time). A fix with NO anchor is a
 *    general/document-level finding — it shows in the list but has no highlight or jump.
 *  - WHY:   `title` + `detail` (+ optional `basis` legal citation).
 *  - HOW:   `action` (human instruction) and, only when auto-applicable, a `fix` with `replacement` text.
 *
 * NONE of this is ever written into the .docx — highlights/cards live in the editor's plugin overlay layer.
 * The ONLY thing that edits the document is pressing Apply (a plain range replacement).
 */
export type FixSeverity = 'critical' | 'important' | 'suggestion';

/** Sort weight — cards/list order by severity first (lower = more severe), then document order. */
export const SEVERITY_RANK: Record<FixSeverity, number> = { critical: 0, important: 1, suggestion: 2 };

/**
 * Shared severity palette:
 *  - `color`     solid accent (pill background, card left accent)
 *  - `fill`      the document highlight (fill only, no underline)
 *  - `fillHover` a lighter highlight shown while its card is hovered
 *  - `bg`        the card's severity-tinted background
 */
export const SEVERITY_TONE: Record<
  FixSeverity,
  { label: string; color: string; fill: string; fillHover: string; bg: string }
> = {
  critical: {
    label: 'Critical',
    color: '#c0392b',
    fill: 'rgba(192, 57, 43, 0.24)',
    fillHover: 'rgba(192, 57, 43, 0.11)',
    bg: '#fcf0ee',
  },
  important: {
    label: 'Important',
    color: '#b7791f',
    fill: 'rgba(214, 158, 46, 0.30)',
    fillHover: 'rgba(214, 158, 46, 0.14)',
    bg: '#fbf3e1',
  },
  suggestion: {
    label: 'Suggestion',
    color: '#2f855a',
    fill: 'rgba(56, 161, 105, 0.22)',
    fillHover: 'rgba(56, 161, 105, 0.10)',
    bg: '#edf6f0',
  },
};

export interface FixAnchor {
  /** The exact target text to locate (the "search"). Omit the whole anchor for a document-level finding. */
  quote: string;
  /** 1-based: which occurrence of `quote` to target when it repeats. Default 1. */
  occurrence?: number;
}

/**
 * A machine-applicable fix operation. The finding's `anchor` gives the reference span; the operation says what
 * to do to it. These three primitives cover most automatic edits and compose into complex ones:
 *  - replace: swap the anchored span for `text`
 *  - remove:  delete the anchored span
 *  - insert:  add `text` immediately before/after the anchored span (e.g. a missing clause after a section)
 */
export type FixOperation =
  | { kind: 'replace'; text: string }
  | { kind: 'remove' }
  | { kind: 'insert'; where: 'before' | 'after'; text: string };

export interface DocumentFix {
  id: string;
  severity: FixSeverity;
  /** Absent → a general finding with no place in the document. */
  anchor?: FixAnchor;
  title: string;
  detail: string;
  basis?: { act: string; section?: string; url?: string };
  action: string;
  /** The automatic operation, if any (requires an anchor). Absent → the fix needs a human. */
  fix?: FixOperation;
  /** Button label; defaults per operation kind (Apply / Insert / Remove). */
  fixLabel?: string;
}

/** The Apply-button label for a fix (explicit `fixLabel`, else a sensible default per operation kind). */
export function fixButtonLabel(fix: DocumentFix): string {
  if (fix.fixLabel) return fix.fixLabel;
  switch (fix.fix?.kind) {
    case 'insert':
      return 'Insert';
    case 'remove':
      return 'Remove';
    default:
      return 'Apply';
  }
}

/** A finding whose anchor has been resolved to a live ProseMirror range (or null if it couldn't be located). */
export interface ResolvedFix {
  fix: DocumentFix;
  range: { from: number; to: number } | null;
}

/**
 * Resolve an anchor to a live ProseMirror `{from,to}` by searching the document text. Searches within each
 * text block so a quote can't spuriously match across a paragraph boundary. Returns null when the quote isn't
 * found — the finding then degrades to list-only (no highlight/jump). Production will resolve via the editor's
 * `w14:paraId` + search agent instead of a raw text scan, but the UI contract (a resolved range) is identical.
 */
export function resolveFixRange(doc: PMNode, anchor: FixAnchor | undefined): { from: number; to: number } | null {
  if (!anchor?.quote) return null;
  const quote = anchor.quote;
  const want = anchor.occurrence ?? 1;
  let seen = 0;
  let result: { from: number; to: number } | null = null;

  doc.descendants((node, pos) => {
    if (result) return false;
    if (!node.isTextblock) return true;

    // Flatten this block's text and remember each character's absolute PM position.
    let text = '';
    const posOf: number[] = [];
    node.descendants((child, childPos) => {
      if (child.isText && child.text) {
        for (let i = 0; i < child.text.length; i++) {
          text += child.text[i];
          posOf.push(pos + 1 + childPos + i);
        }
      }
      return true;
    });

    let from = 0;
    let idx: number;
    while ((idx = text.indexOf(quote, from)) !== -1) {
      seen += 1;
      if (seen === want) {
        result = { from: posOf[idx], to: posOf[idx + quote.length - 1] + 1 };
        return false;
      }
      from = idx + 1;
    }
    return false; // already gathered this block's text; don't descend again
  });

  return result;
}

export function resolveFixes(doc: PMNode, fixes: DocumentFix[]): ResolvedFix[] {
  return fixes.map((fix) => ({ fix, range: resolveFixRange(doc, fix.anchor) }));
}

/**
 * Hardcoded findings for the spike — chosen to exercise every UI case against the sample document
 * ("Disciplinary policy and procedure / Rivera Coffee Roasters / This sample .docx is open in the
 * docx-editor.dev editor. You can type here."):
 *  - general, NO location + no fix (manual)         → list-only
 *  - remove   (delete the vendor phrase)
 *  - replace  (swap in ACAS wording / the registered name)
 *  - insert   (add the statutory accompaniment sentence after the snippet)
 */
export const STUB_FIXES: DocumentFix[] = [
  {
    id: 'no-appeal',
    severity: 'important',
    title: 'No right of appeal',
    detail:
      'The policy never sets out an appeal stage. The ACAS Code of Practice requires that employees can appeal a disciplinary decision, and a tribunal can raise an award by up to 25% where it was not followed.',
    basis: { act: 'ACAS Code of Practice on Disciplinary and Grievance Procedures', section: '§26' },
    action: 'Add an “Appeals” section after the sanctions setting out how and when an employee can appeal.',
    // no anchor → general finding; no fix → manual
  },
  {
    id: 'placeholder',
    severity: 'suggestion',
    anchor: { quote: 'docx-editor.dev ' },
    title: 'Vendor placeholder text',
    detail: 'The document still names the editor vendor, and that reference should not appear in a real policy.',
    action: 'Remove the vendor reference.',
    fix: { kind: 'remove' },
  },
  {
    id: 'informal-stage',
    severity: 'critical',
    anchor: { quote: 'This sample .docx is open in the docx-editor.dev editor.' },
    title: 'No informal stage described',
    detail:
      'A lawful procedure distinguishes minor misconduct (handled informally) from serious misconduct (formal action). This clause does neither.',
    basis: { act: 'ACAS Code of Practice', section: '§1' },
    action: 'State that minor issues are handled informally and serious issues follow the formal procedure.',
    fix: {
      kind: 'replace',
      text: 'Minor misconduct will normally be dealt with informally. Serious misconduct may lead to formal action under this procedure, up to and including dismissal.',
    },
    fixLabel: 'Insert ACAS-aligned wording',
  },
  {
    id: 'accompaniment',
    severity: 'important',
    anchor: { quote: 'You can type here.' },
    title: 'Right to be accompanied missing',
    detail:
      'Employees have a statutory right to be accompanied at a formal disciplinary hearing by a colleague or trade union representative. The policy must say so.',
    basis: { act: 'Employment Relations Act 1999', section: 's.10' },
    action: 'State the right to be accompanied at any formal hearing.',
    fix: {
      kind: 'insert',
      where: 'after',
      text: ' You have the right to be accompanied at any formal hearing by a work colleague or a trade union representative.',
    },
    fixLabel: 'Insert statutory wording',
  },
  {
    id: 'legal-name',
    severity: 'suggestion',
    anchor: { quote: 'Rivera Coffee Roasters' },
    title: 'Use the registered legal name',
    detail: 'Policies should name the employing entity by its full registered name so it is clear who they bind.',
    action: 'Use the full registered company name including the suffix.',
    fix: { kind: 'replace', text: 'Rivera Coffee Roasters Ltd' },
    fixLabel: 'Use registered name',
  },
];
